import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Award, Target, Briefcase, GraduationCap, Calendar, MapPin, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({ name: '', email: '', phone: '' });
  const [isRegistering, setIsRegistering] = useState(false);

  // Hardcoded event - no database dependency
  const events = [
    {
      id: "financial-freedom-webinar-2026",
      title: "Financial Freedom Webinar",
      description: "Join us online for an exclusive webinar on achieving financial independence. Learn from success stories and practical strategies.",
      date: "March 29, 2026",
      time: "1:00 PM - 2:00 PM (EST)",
      location: "Online (Zoom)",
      event_type: "webinar",
      capacity: 40,
      registrations_count: 0,
      image_url: "https://images.pexels.com/photos/8276364/pexels-photo-8276364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    }
  ];

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    
    setIsRegistering(true);
    try {
      await axios.post(`${API}/events/register`, {
        event_id: selectedEvent.id,
        ...registrationForm
      });
      toast.success('Successfully registered for the event!');
      setIsDialogOpen(false);
      setRegistrationForm({ name: '', email: '', phone: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsRegistering(false);
    }
  };

  const stats = [
    { value: '4+', label: 'Years Experience', icon: Award },
    { value: '400+', label: 'Families Empowered', icon: Users },
    { value: '99%', label: 'Client Satisfaction', icon: TrendingUp },
  ];

  const services = [
    {
      icon: TrendingUp,
      title: 'Financial Planning',
      description: 'Strategic planning to secure your financial future and achieve your goals.',
    },
    {
      icon: Briefcase,
      title: 'Wealth Building',
      description: 'Proven strategies to grow and protect your wealth across generations.',
    },
    {
      icon: GraduationCap,
      title: 'Leadership Development',
      description: 'Transform into an influential leader with our comprehensive programs.',
    },
    {
      icon: Target,
      title: 'Mentorship Programs',
      description: 'One-on-one guidance from industry experts to accelerate your success.',
    },
  ];

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1759178461753-66bac61b149c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxibGFjayUyMGFuZCUyMGdvbGQlMjBhYnN0cmFjdCUyMGx1eHVyeSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcxNjUyMzA1fDA&ixlib=rb-4.1.0&q=85"
            alt="Luxury Background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-4xl">
            <p 
              className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-6 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.1s' }}
            >
              Leadership • Wealth • Success
            </p>
            <h1 
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-tight mb-8 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.3s' }}
            >
              Build Your <span className="text-gold-gradient">Legacy</span> of{' '}
              <span className="italic">Wealth</span> & Leadership
            </h1>
            <p 
              className="font-body text-white/70 text-base sm:text-lg max-w-2xl mb-12 leading-relaxed opacity-0 animate-fade-in"
              style={{ animationDelay: '0.5s' }}
            >
              Empower Wealth Builders transforms ambitious individuals into financially free leaders through expert guidance, proven strategies, and a supportive community where we help people on financial education - we believe financial literacy is universal where every family can build wealth.
            </p>
            <div 
              className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.7s' }}
            >
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center justify-center gap-2 bg-gold-500 text-black px-8 py-4 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300"
              >
                Explore Services
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 font-body font-semibold uppercase tracking-widest hover:border-gold-500 hover:text-gold-500 transition-colors duration-300"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-px h-16 bg-gradient-to-b from-gold-500 to-transparent" />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-[#050505] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
            {stats.map((stat, index) => (
              <div 
                key={index}
                data-testid={`stat-${index}`}
                className="text-center p-8 border border-white/5 hover:border-gold-500/30 transition-colors duration-500 group"
              >
                <stat.icon className="w-8 h-8 text-gold-500 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
                <div className="font-heading text-5xl sm:text-6xl text-gold-500 mb-2">{stat.value}</div>
                <div className="font-body text-white/60 uppercase tracking-widest text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">What We Offer</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Our <span className="text-gold-gradient">Services</span>
            </h2>
            <p className="font-body text-white/60 max-w-2xl text-base sm:text-lg">
              Comprehensive solutions designed to transform your financial future and leadership capabilities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="group p-8 lg:p-10 bg-white/[0.02] border border-white/5 hover:border-gold-500/30 transition-all duration-500"
              >
                <service.icon className="w-10 h-10 text-gold-500 mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-heading text-xl sm:text-2xl text-white mb-4">{service.title}</h3>
                <p className="font-body text-white/50 leading-relaxed mb-6">{service.description}</p>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 font-body text-sm text-gold-500 uppercase tracking-widest hover:gap-3 transition-all duration-300"
                >
                  Learn More
                  <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="font-body text-gold-500 uppercase tracking-[0.3em] text-sm mb-4">Upcoming Events</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Seminars & <span className="text-gold-gradient">Workshops</span>
            </h2>
            <p className="font-body text-white/60 max-w-2xl text-base sm:text-lg">
              Join our transformative events and learn directly from industry experts.
            </p>
          </div>

          {events.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {events.map((event, index) => (
                <div
                  key={event.id}
                  data-testid={`event-card-${index}`}
                  className="group bg-white/[0.02] border border-white/5 hover:border-gold-500/30 overflow-hidden transition-all duration-500"
                >
                  {event.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="px-3 py-1 bg-gold-500/10 text-gold-500 text-xs font-body uppercase tracking-wider">
                        {event.event_type}
                      </span>
                      <span className="text-white/40 text-xs font-body">
                        {event.capacity - event.registrations_count} spots left
                      </span>
                    </div>
                    <h3 className="font-heading text-xl text-white mb-3">{event.title}</h3>
                    <p className="font-body text-white/50 text-sm mb-6 line-clamp-2">{event.description}</p>
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center gap-2 text-white/40 text-sm font-body">
                        <Calendar size={14} className="text-gold-500" />
                        {event.date}
                      </div>
                      <div className="flex items-center gap-2 text-white/40 text-sm font-body">
                        <Clock size={14} className="text-gold-500" />
                        {event.time}
                      </div>
                      <div className="flex items-center gap-2 text-white/40 text-sm font-body">
                        <MapPin size={14} className="text-gold-500" />
                        {event.location}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedEvent(event);
                        setIsDialogOpen(true);
                      }}
                      data-testid={`register-event-btn-${index}`}
                      className="w-full bg-gold-500 text-black py-3 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300"
                    >
                      Register Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/5">
              <p className="font-body text-white/50">No upcoming events at the moment. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Ready to <span className="text-gold-gradient">Transform</span> Your Future?
            </h2>
            <p className="font-body text-white/60 text-base sm:text-lg mb-12">
              Join thousands of individuals who have taken control of their financial destiny with Empower Wealth Builders.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/careers"
                data-testid="cta-join-btn"
                className="inline-flex items-center justify-center gap-2 bg-gold-500 text-black px-10 py-4 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300"
              >
                Join Our Team
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/services"
                data-testid="cta-services-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-10 py-4 font-body font-semibold uppercase tracking-widest hover:border-gold-500 hover:text-gold-500 transition-colors duration-300"
              >
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Event Registration Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white">
              Register for Event
            </DialogTitle>
            <DialogDescription className="font-body text-white/60">
              {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-6 mt-4">
            <div>
              <label className="block font-body text-sm text-white/70 mb-2">Full Name</label>
              <input
                type="text"
                required
                value={registrationForm.name}
                onChange={(e) => setRegistrationForm({ ...registrationForm, name: e.target.value })}
                data-testid="registration-name-input"
                className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block font-body text-sm text-white/70 mb-2">Email Address</label>
              <input
                type="email"
                required
                value={registrationForm.email}
                onChange={(e) => setRegistrationForm({ ...registrationForm, email: e.target.value })}
                data-testid="registration-email-input"
                className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block font-body text-sm text-white/70 mb-2">Phone (Optional)</label>
              <input
                type="tel"
                value={registrationForm.phone}
                onChange={(e) => setRegistrationForm({ ...registrationForm, phone: e.target.value })}
                data-testid="registration-phone-input"
                className="w-full bg-white/5 border border-white/10 px-4 py-3 font-body text-white placeholder:text-white/40 focus:outline-none focus:border-gold-500/50"
                placeholder="+1 (234) 567-890"
              />
            </div>
            <button
              type="submit"
              disabled={isRegistering}
              data-testid="registration-submit-btn"
              className="w-full bg-gold-500 text-black py-4 font-body font-semibold uppercase tracking-widest hover:bg-gold-400 transition-colors duration-300 disabled:opacity-50"
            >
              {isRegistering ? 'Registering...' : 'Complete Registration'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
